import { useFinancialCaptureContext } from '@/hooks/useFinancialCaptureContext';
// src/components/Invoices/InvoiceUpload.jsx

import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/axios";
import { Loader2, ArrowLeft, ExternalLink, UploadCloud } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

function fmtRecordedAmount(value) {
  const text=String(value??'');
  if(!/^-?\d+(?:\.\d{1,2})?$/.test(text))return 'Unavailable';
  const [whole,fraction='']=text.split('.');return whole.replace(/\B(?=(\d{3})+(?!\d))/g,',')+'.'+fraction.padEnd(2,'0');
}

function fmtDate(d) {
  try {
    return d ? new Date(d).toLocaleString() : "—";
  } catch {
    return "—";
  }
}

function StatusBadge({ text }) {
  const t = String(text || "").toLowerCase();

  let cls = "border-white/15 text-white";

  if (["paid", "approved", "posted"].includes(t)) {
    cls = "bg-emerald-900/40 border-emerald-800/40 text-emerald-200";
  } else if (["pending", "processing", "unpaid"].includes(t)) {
    cls = "bg-amber-900/40 border-amber-800/40 text-amber-200";
  } else if (["rejected", "failed"].includes(t)) {
    cls = "bg-rose-900/40 border-rose-800/40 text-rose-200";
  }

  return <Badge className={cls}>{text || "—"}</Badge>;
}

export default function InvoiceUploadPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const capture = useFinancialCaptureContext();
  const captureIdentity = useRef(capture.identity); captureIdentity.current = capture.identity;
  const [invoiceCurrency,setInvoiceCurrency] = useState('');
  const financialRequest = useRef(null), activeUpload = useRef(null);

  const isDetailMode = Boolean(id);
  const isUploadMode = !id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(isDetailMode);
  const [errMsg, setErrMsg] = useState("");

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadInfo, setUploadInfo] = useState(null);

  useEffect(() => {
    if (!isDetailMode) return;

    let cancel = false;

    async function loadInvoiceDetail() {
      setLoading(true);
      setErrMsg("");

      try {
        const res = await api.get(`/invoice/view/${id}`);
        if (!cancel) setData(res.data || null);
      } catch (err) {
        console.error("Error loading invoice detail:", err);

        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load invoice detail.";

        if (!cancel) setErrMsg(msg);
        if (err?.response?.status === 403) navigate("/forbidden");
      } finally {
        if (!cancel) setLoading(false);
      }
    }

    loadInvoiceDetail();

    return () => {
      cancel = true;
    };
  }, [id, isDetailMode, navigate]);

  useEffect(() => {
    activeUpload.current?.abort();setFile(null);setInvoiceCurrency('');setUploadInfo(null);setUploadError('');setUploading(false);financialRequest.current=null;
    return()=>activeUpload.current?.abort();
  },[capture.identity]);
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0] || null;

    setFile(selectedFile);financialRequest.current=null;
    setUploadError("");
    setUploadInfo(null);
  };

  const handleUpload = async () => {
    if (!file) {
      setUploadError("Please choose a PDF or image to upload.");
      return;
    }

    if (capture.loading || capture.error) { setUploadError('Refresh the unit settings before uploading.');return; }
    if (capture.data && !invoiceCurrency) { setUploadError('Select the original invoice currency, or choose unknown.');return; }
    const identity=capture.identity, controller=new AbortController();activeUpload.current=controller;
    if (!financialRequest.current) financialRequest.current=crypto.randomUUID();
    setUploading(true);
    setUploadError("");
    setUploadInfo(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      if(capture.data){formData.append('financial_context_version','1');formData.append('invoice_currency',invoiceCurrency);formData.append('financial_request_id',financialRequest.current);}

      const res = await api.post("/invoice/upload", formData, {
        signal:controller.signal,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if(controller.signal.aborted||captureIdentity.current!==identity)return;
      setUploadInfo(res?.data || {});

      setTimeout(() => {
        if(captureIdentity.current===identity&&!controller.signal.aborted)navigate("/invoice/approval");
      }, 800);
    } catch (err) {
      if(controller.signal.aborted||captureIdentity.current!==identity)return;
      console.error("Failed to upload invoice:", err);

      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.details ||
        err?.message ||
        "Upload failed. Please try again.";

      setUploadError(msg);
    } finally {
      if(captureIdentity.current===identity)setUploading(false);
    }
  };

  if (isUploadMode) {
    return (
      <div className="min-h-screen bg-slate-950 text-white px-6 py-8">
        <div className="max-w-5xl mx-auto space-y-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate("/invoice/approval")}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 border border-white/10 text-sm"
              >
                <ArrowLeft size={16} />
                Back to Invoice Dashboard
              </button>

              <div>
                <h1 className="text-2xl md:text-3xl font-bold">
                  🧾 Upload Supplier Invoice
                </h1>
                <p className="text-sm text-slate-300">
                  Drop a PDF or image and SOLO will store it ready for PO,
                  receipt, and variance analysis.
                </p>
              </div>
            </div>
          </div>

          <Card className="bg-slate-900/80 border-slate-700/70 p-6 md:p-8">
            <div className="grid md:grid-cols-3 gap-6 items-start">
              <div className="md:col-span-2">
                <label
                  htmlFor="invoice-file"
                  className="flex flex-col items-center justify-center w-full border-2 border-dashed border-slate-600/80 rounded-2xl px-4 py-10 cursor-pointer hover:border-sky-500/70 hover:bg-slate-800/40 transition"
                >
                  <UploadCloud className="h-10 w-10 mb-3 text-sky-400" />

                  <p className="text-sm font-medium">
                    Click to choose a file or drag &amp; drop
                  </p>

                  <p className="text-xs text-slate-400 mt-1">
                    PDF, PNG, JPG · up to 15MB
                  </p>

                  {file && (
                    <p className="mt-3 text-xs text-sky-300">
                      Selected: <span className="font-mono">{file.name}</span>
                    </p>
                  )}

                  <input
                    id="invoice-file"
                    type="file"
                    accept="application/pdf,image/*"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>
              </div>

              <div className="space-y-3 text-sm">
                {capture.data&&<label className="block text-sm text-slate-200">Original invoice currency
                  <select aria-label="Original invoice currency" value={invoiceCurrency} onChange={e=>{setInvoiceCurrency(e.target.value);financialRequest.current=null;}} disabled={uploading}
                    className="mt-2 w-full rounded-lg border border-white/15 bg-slate-950 px-3 py-2.5 text-sm">
                    <option value="">Select from the supplier invoice</option><option value="unknown">Unknown — review later</option>{capture.data.currencies.map(c=><option key={c}>{c}</option>)}
                  </select><span className="mt-2 block text-xs leading-relaxed text-slate-400">Use the currency printed on the invoice. Unknown amounts stay outside currency totals until confirmed.</span>
                </label>}
                {capture.error&&<p role="alert" className="text-sm text-rose-300">{capture.error}</p>}
                <h2 className="font-semibold text-slate-100">
                  What happens next?
                </h2>

                <ul className="list-disc list-inside text-slate-300 space-y-1.5">
                  <li>Invoice file is stored against the invoice ledger.</li>
                  <li>The upload record is linked to the invoice record.</li>
                  <li>PO reconciliation will run when PO and amount data are available.</li>
                  <li>Finance can review it from the invoice dashboard.</li>
                </ul>

                {uploadError && (
                  <p className="text-xs text-rose-400 mt-2">❌ {uploadError}</p>
                )}

                {uploadInfo && !uploadError && (
                  <p className="text-xs text-emerald-400 mt-2">
                    ✅ Uploaded successfully. Redirecting to invoice dashboard…
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={uploading || !file || capture.loading || Boolean(capture.error)}
                  className="mt-2 inline-flex items-center justify-center px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 disabled:opacity-50 disabled:cursor-not-allowed w-full"
                >
                  {uploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {uploading ? "Uploading…" : "Upload Invoice"}
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 text-white flex items-center gap-2">
        <Loader2 className="animate-spin" />
        Loading invoice detail…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8 text-red-400">
        ❌ {errMsg || "Failed to load invoice detail."}
      </div>
    );
  }

  const {
    invoice = {},
    vendor = {},
    upload = {},
    extracted_lines = [],
    approvals = [],
  } = data;

  const fileUrl =
    upload?.file_url ||
    upload?.public_url ||
    upload?.url ||
    upload?.path ||
    "";

  const pagesHint =
    Array.isArray(upload?.pages) && upload.pages.length > 0
      ? ` · ${upload.pages.length} page(s)`
      : "";

  return (
    <div className="p-8 text-white">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700 border border-white/10 text-sm"
          >
            <ArrowLeft size={16} />
            Back
          </button>

          <h1 className="text-2xl md:text-3xl font-bold">
            🧾 Invoice #{invoice.invoice_id ?? id}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          {invoice.payment_status && <StatusBadge text={invoice.payment_status} />}
          {invoice.approval_status && <StatusBadge text={invoice.approval_status} />}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card className="bg-neutral-800 p-4">
          <h2 className="font-semibold text-lg mb-2">📄 Invoice Metadata</h2>

          <div className="space-y-1 text-sm">
            <p>
              <strong>Invoice #:</strong>{" "}
              {invoice.invoice_number || invoice.reference || "—"}
            </p>

            <p>
              <strong>Due Date:</strong> {fmtDate(invoice.due_date)}
            </p>

            <p>
              <strong>Amount Due:</strong>{" "}
              {fmtRecordedAmount(invoice.amount_due ?? invoice.total_due)}
            </p>

            <p>
              <strong>Uploaded:</strong>{" "}
              {fmtDate(invoice.uploaded_at || upload?.uploaded_at)}
            </p>

            {fileUrl && (
              <p className="pt-1">
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-cyan-300 hover:underline"
                >
                  View original{pagesHint} <ExternalLink size={14} />
                </a>
              </p>
            )}
          </div>
        </Card>

        <Card className="bg-neutral-800 p-4">
          <h2 className="font-semibold text-lg mb-2">🏢 Vendor Info</h2>

          <div className="space-y-1 text-sm">
            <p>
              <strong>Name:</strong>{" "}
              {vendor?.vendor_name || upload?.vendor_name || "—"}
            </p>
            <p>
              <strong>Email:</strong> {vendor?.contact_email || "—"}
            </p>
            <p>
              <strong>Phone:</strong> {vendor?.phone || "—"}
            </p>
            <p className="break-words">
              <strong>Address:</strong> {vendor?.address || "—"}
            </p>
          </div>
        </Card>
      </div>

      {Array.isArray(extracted_lines) && extracted_lines.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-2 text-cyan-300">
            📦 Extracted Invoice Line Items
          </h2>

          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-neutral-900">
                <tr>
                  <th className="p-3 text-left">Description</th>
                  <th className="p-3 text-left">Code</th>
                  <th className="p-3 text-right">Qty</th>
                  <th className="p-3 text-right">Unit £</th>
                  <th className="p-3 text-right">Total £</th>
                </tr>
              </thead>

              <tbody>
                {extracted_lines.map((line, idx) => {
                  const qty = Number(line.quantity);
                  const unit = Number(line.unit_price);
                  const total = Number(line.total_line_value);

                  return (
                    <tr key={idx} className="border-t border-white/10">
                      <td className="p-3">{line.product_description || "—"}</td>
                      <td className="p-3 font-mono">{line.product_code || "—"}</td>
                      <td className="p-3 text-right">
                        {Number.isFinite(qty) ? qty : "—"}
                      </td>
                      <td className="p-3 text-right">
                        {Number.isFinite(unit) ? unit.toFixed(2) : "—"}
                      </td>
                      <td className="p-3 text-right">
                        {Number.isFinite(total) ? total.toFixed(2) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {Array.isArray(approvals) && approvals.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-2 text-green-400">
            ✅ Approval History
          </h2>

          <ul className="space-y-2">
            {approvals.map((a, i) => (
              <li
                key={i}
                className="bg-neutral-900 p-3 rounded-lg border border-neutral-700"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">
                    {a.approved_by_name || a.user_name || "—"}
                  </span>
                  <span className="text-xs text-gray-300">
                    {fmtDate(a.decision_at)}
                  </span>
                </div>

                <p className="mt-1 text-sm text-gray-300">
                  Decision: <strong>{a.decision || "—"}</strong>
                  {a.notes ? ` – ${a.notes}` : " – No notes"}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-8 flex items-center gap-3 text-sm">
        <Link
          to="/invoice/approval"
          className="px-3 py-2 rounded bg-gray-800 hover:bg-gray-700 border border-white/10"
        >
          Back to Invoice Dashboard
        </Link>

        {!!invoice?.po_id && (
          <Link
            to={`/po-receiving/${invoice.po_id}`}
            className="px-3 py-2 rounded bg-blue-800 hover:bg-blue-700 border border-white/10"
            title="Open related PO receiving"
          >
            Go to related PO
          </Link>
        )}
      </div>
    </div>
  );
}
